import Button from '@app-studio-web/ui/components/Button'
import Heading from '@app-studio-web/ui/components/Heading'
import PageLayout from '@app-studio-web/ui/components/PageLayout'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form } from 'react-router-dom'

interface MeasurementPageProps {
  heading: string
  description: string
  placeholders: Record<string, string>
  units: string[]
  defaultUnit?: string
  onSubmit: (
    value: string,
    unit: string,
    event: React.FormEvent<HTMLFormElement>,
  ) => void
  preselectedValue?: string
}

export default function MeasurementPage({
  heading,
  description,
  placeholders,
  units,
  defaultUnit,
  onSubmit,
  preselectedValue,
}: MeasurementPageProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState(preselectedValue || '')
  const [unit, setUnit] = useState(defaultUnit || units[0])
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!value || isNaN(Number(value)) || Number(value) <= 0) return
    onSubmit(value, unit, event)
  }

  const handleInputContainerClick = () => {
    inputRef.current?.focus()
  }

  return (
    <PageLayout>
      <Form
        className="flex grow flex-col"
        method="post"
        onSubmit={handleSubmit}
      >
        <div className="shrink-0">
          <Heading
            title={t(heading)}
            subtitle={description ? t(description) : undefined}
          />
        </div>

        <div className="flex grow flex-col items-center justify-center">
          <div className="flex w-full max-w-[334px] flex-col gap-4">
            {/* Unit Toggle */}
            <div className="relative h-12 w-full rounded-3xl border border-stone-200 bg-[#fdfbf7]">
              <div
                className="absolute top-[5px] h-[38px] w-[calc(50%-5px)] rounded-3xl bg-neutral-800 transition-all duration-200"
                style={{
                  left: unit === units[0] ? '5px' : 'calc(50%)',
                }}
              />
              <div className="relative flex h-full items-center">
                {units.map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setUnit(u)}
                    className={`z-10 flex-1 text-base font-bold transition-colors ${
                      unit === u ? 'text-white' : 'text-neutral-400'
                    }`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>

            {/* Measurement Input */}
            <div
              onClick={handleInputContainerClick}
              className="flex h-[100px] w-full cursor-text flex-col items-center justify-center rounded-3xl border-2 border-gray-200 bg-white"
            >
              <input
                ref={inputRef}
                type="number"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={placeholders[unit] || ''}
                className="w-full border-0 bg-transparent text-center text-3xl placeholder:text-neutral-400 focus:ring-0 focus:outline-none"
              />
              <span className="text-sm text-neutral-400">{unit}</span>
            </div>
          </div>
        </div>

        <div>
          <Button
            title={t('buttons.continue')}
            type="submit"
            data-testid="button-continue-input"
            disabled={!value || isNaN(Number(value)) || Number(value) <= 0}
          />
        </div>
      </Form>
    </PageLayout>
  )
}
